import { useEffect, useRef } from 'react';

import type { GapDraftView } from '@momentum/types';
import { GapDraftReviewStatus } from '@momentum/types';

import { dateTime } from '../../lib/format';

const STATUS_LABEL: Record<GapDraftReviewStatus, string> = {
  [GapDraftReviewStatus.PENDING]: 'Not reviewed',
  [GapDraftReviewStatus.APPROVED]: 'Kept',
  [GapDraftReviewStatus.REJECTED]: 'Dropped',
};

interface NotesTabProps {
  draft: GapDraftView;
  /** The unposted comment text for this draft, held by the route. */
  commentDraft: string;
  onCommentDraftChange: (text: string) => void;
  onPost: () => void;
  onDiscard: () => void;
  isPosting: boolean;
  /**
   * Bumped whenever the reviewer asks to compose — by button or by `C`. A token
   * rather than a ref because `C` also switches to this tab, so the request can
   * arrive before the textarea exists.
   */
  focusToken: number;
  /**
   * The quotes the reader's document currently contains. A comment whose quote
   * is missing from this set is one whose passage has been edited away, or one
   * written against the reader's other view.
   */
  matchedQuotes: ReadonlySet<string>;
  onRevealQuote: (quote: string) => void;
  /**
   * The passage the reviewer right-clicked, if they did. Held by the route
   * beside `commentDraft` — the two are one unposted comment.
   */
  commentQuote: string | undefined;
  onClearQuote: () => void;
}

/**
 * Notes tab — the review record and the comment thread.
 *
 * SPEC §4.5 put comments in a 420px drawer. D's rail is permanent, so the drawer
 * became this tab: a drawer over a permanent rail would cover the thing the
 * reviewer is commenting on.
 *
 * Comments are free text with no tags and no threading. One reviewer, appending
 * as often as they like — the audience is whoever reads the run afterwards, and
 * structure they would have to learn is worse than a paragraph.
 */
export function NotesTab({
  draft,
  commentDraft,
  onCommentDraftChange,
  onPost,
  onDiscard,
  isPosting,
  focusToken,
  matchedQuotes,
  onRevealQuote,
  commentQuote,
  onClearQuote,
}: NotesTabProps) {
  const { review } = draft;
  const composeRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (focusToken === 0) return;

    const textarea = composeRef.current;
    if (textarea === null) return;

    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
  }, [focusToken]);

  const isDirty = commentDraft.trim().length > 0;

  return (
    <>
      <div>
        <h3 className="gr-insp-h">Review</h3>
        <div className="gr-facts gr-facts-flush">
          <div className="gr-fact">
            <span>status</span>
            <span className="gr-fact-v">{STATUS_LABEL[review.status]}</span>
          </div>
          <div className="gr-fact">
            <span>reviewer</span>
            <span className="gr-fact-v mono">
              {review.reviewerName ?? review.employeeId ?? '—'}
            </span>
          </div>
          <div className="gr-fact">
            <span>decided at</span>
            <span className="gr-fact-v mono">{dateTime(review.decidedAt)}</span>
          </div>
        </div>
      </div>

      <div>
        <h3 className="gr-insp-h">Comments · {review.comments.length}</h3>
        <div className="gr-comments">
          {review.comments.length > 0 ? (
            review.comments.map((comment) => {
              const quote = comment.quote;

              return (
                <div className="gr-comment" key={comment._id}>
                  <div className="gr-comment-top">
                    <span className="gr-comment-who">
                      {comment.authorName ?? comment.employeeId}
                    </span>
                    <span className="gr-comment-when">
                      {dateTime(comment.createdAt)}
                    </span>
                  </div>
                  {/* The quote sits ABOVE the comment, always, whether or not it
                    can still be found on screen. It is what the comment is
                    about, and a reader who cannot see it has read a reply to
                    nothing. Clickable only when it is findable — the button
                    scrolls the reader to it — and otherwise the same words with
                    a note saying the text has moved on since. */}
                  {quote !== undefined &&
                    (matchedQuotes.has(quote) ? (
                      <button
                        className="gr-comment-quote"
                        type="button"
                        onClick={() => onRevealQuote(quote)}
                        title="Show this passage in the gap"
                      >
                        {quote}
                      </button>
                    ) : (
                      <p className="gr-comment-quote gr-comment-quote-stale">
                        {quote}
                        <span className="gr-comment-quote-note">
                          not in the text as it reads now
                        </span>
                      </p>
                    ))}
                  <p className="gr-comment-body">{comment.text}</p>
                </div>
              );
            })
          ) : (
            <div className="gr-empty">
              <b>No comments</b>Select text in the gap and right-click it to
              comment on that passage, or write below about the gap as a whole.
            </div>
          )}

          <div className="gr-compose">
            {/* Right-clicking a passage in the reader drops it in here and puts
                the cursor below it — the same box, one comment either way. The
                × detaches it rather than discarding what has been typed: a
                reviewer who decides the point is about the whole gap should not
                have to retype the sentence to say so. */}
            {commentQuote !== undefined && (
              <div className="gr-compose-quote">
                <p className="gr-compose-quote-text">{commentQuote}</p>
                <button
                  className="gr-compose-quote-clear"
                  type="button"
                  onClick={onClearQuote}
                  title="Comment on the whole gap instead"
                  aria-label="Remove the quoted passage"
                >
                  ×
                </button>
              </div>
            )}
            <textarea
              ref={composeRef}
              value={commentDraft}
              placeholder={
                commentQuote === undefined
                  ? 'Why keep it, why drop it, what to check…'
                  : "What's wrong with this, or how it should read…"
              }
              onChange={(changeEvent) =>
                onCommentDraftChange(changeEvent.target.value)
              }
            />
            <div className="gr-compose-foot">
              <span className="gr-compose-hint">
                <span className="gr-kbd">⌘</span>
                <span className="gr-kbd">↵</span> post
                {isDirty && (
                  <>
                    {' · '}
                    <span className="gr-dirty">auto-advance held</span>
                  </>
                )}
              </span>
              <span className="gr-compose-acts">
                <button
                  className="gr-btn-ghost"
                  type="button"
                  onClick={onDiscard}
                >
                  Discard
                </button>
                <button
                  className="gr-btn-sm"
                  type="button"
                  onClick={onPost}
                  disabled={!isDirty || isPosting}
                >
                  {isPosting ? 'Posting…' : 'Post comment'}
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
